import { Component } from '@theme/component';
import { CompareStore } from '@theme/compare-store';

/**
 * CompareBar — floating bar at the bottom of collection/search pages.
 *
 * Reads product handles from CompareStore, fetches product JSON from
 * Shopify's public product API, and renders thumbnails.
 *
 * @typedef {object} CompareBarRefs
 * @property {HTMLElement} countBadge - The count badge span
 * @property {HTMLUListElement} thumbList - The thumbnail list
 * @property {HTMLElement} content - The thumbnails container (for mobile toggle)
 * @property {HTMLButtonElement} clearBtn - The clear-all button
 * @property {HTMLAnchorElement} ctaLink - The compare CTA link
 *
 * @extends {Component<CompareBarRefs>}
 */
export class CompareBar extends Component {
  requiredRefs = ['countBadge', 'thumbList', 'clearBtn', 'ctaLink', 'content'];

  /** Cache for fetched product data to avoid repeated network calls. @type {Map<string, object>} */
  static #productCache = new Map();

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    this.compareUrl = this.dataset.compareUrl || '/pages/compare';

    // Event listeners
    this.refs.clearBtn.addEventListener('click', this);
    this.refs.ctaLink.addEventListener('click', this);
    this.refs.thumbList.addEventListener('click', this);

    // Mobile toggle
    const toggleBtn = this.querySelector('.compare-bar__toggle');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        toggleBtn.setAttribute('aria-expanded', String(!expanded));
        this.refs.content.classList.toggle('is-expanded');
      });
    }

    window.addEventListener('compare:updated', this);

    // Initial render
    this.refresh();
  }

    disconnectedCallback() {
    this.refs.clearBtn?.removeEventListener('click', this);
    this.refs.ctaLink?.removeEventListener('click', this);
    this.refs.thumbList?.removeEventListener('click', this);
    window.removeEventListener('compare:updated', this);
    this.#activeFetch?.abort();
  }

  /**
   * @param {Event} event
   */
  handleEvent(event) {
    switch (event.type) {
      case 'click': {
        const target = /** @type {HTMLElement} */ (event.target);
        // Clear all button
        if (target === this.refs.clearBtn) {
          event.preventDefault();
          CompareStore.clear();
          this.refresh();
          return;
        }
        // CTA link
        if (target === this.refs.ctaLink || target.closest('.compare-bar__cta')) {
          const count = CompareStore.count();
          if (count < 2) {
            event.preventDefault();
          }
          return;
        }
        // Remove single product (event delegation on thumbList)
        const removeBtn = target.closest('.compare-bar__thumb-remove');
        if (removeBtn && removeBtn.dataset.handle) {
          event.preventDefault();
          CompareStore.remove(removeBtn.dataset.handle);
          // refresh happens via compare:updated event
          return;
        }
        break;
      }

      case 'compare:updated':
        this.refresh();
        break;
    }
  }

  /** Re-render the bar based on current store state. */
  async refresh() {
    const handles = CompareStore.getAll();
    const count = handles.length;

    // Update count badge
    this.refs.countBadge.textContent = String(count);

    // Show/hide the bar
    if (count === 0) {
      this.setAttribute('hidden', '');
      return;
    }
    this.removeAttribute('hidden');

    // Enable/disable CTA
    if (count < 2) {
      this.refs.ctaLink.setAttribute('aria-disabled', 'true');
    } else {
      this.refs.ctaLink.removeAttribute('aria-disabled');
    }

    // Fetch and render thumbnails
    this.refs.thumbList.innerHTML = '';
    for (const [index, handle] of handles.entries()) {
      const data = await this.#fetchProductData(handle);
      if (!data) continue;

      const li = document.createElement('li');
      li.className = 'compare-bar__thumb-item';
      li.style.setProperty('--thumb-index', String(index));

      const link = document.createElement('a');
      link.className = 'compare-bar__thumb-link';
      link.href = `/products/${handle}`;
      link.setAttribute('aria-label', data.title || handle);
      link.setAttribute('tabindex', '-1');

      const img = document.createElement('img');
      img.className = 'compare-bar__thumb-img';
      img.src = data.featured_image || '';
      img.alt = data.title || '';
      img.loading = 'lazy';
      img.width = 36;
      img.height = 36;

      link.appendChild(img);
      li.appendChild(link);

      // Remove button
      const removeBtn = document.createElement('button');
      removeBtn.className = 'compare-bar__thumb-remove';
      removeBtn.setAttribute('aria-label', `Remove ${data.title || handle} from comparison`);
      removeBtn.dataset.handle = handle;
      removeBtn.type = 'button';
      removeBtn.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      li.appendChild(removeBtn);

      this.refs.thumbList.appendChild(li);
    }
  }

  /**
   * Fetch product JSON from Shopify's public API.
   * Results are cached in a static Map.
   * @param {string} handle
   * @returns {Promise<object|null>}
   */
  async #fetchProductData(handle) {
    // Check cache first
    const cached = CompareBar.#productCache.get(handle);
    if (cached) return cached;

    // Abort any in-flight request
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(`/products/${handle}.js`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      const product = {
        title: data.title || '',
        featured_image: data.featured_image || '',
        // Store minimal data — we only need image + title for the bar
      };

      CompareBar.#productCache.set(handle, product);
      return product;
    } catch (err) {
      if (err.name === 'AbortError') return null;
      // Silently fail — the bar just won't show that thumbnail
      return null;
    }
  }
}

if (!customElements.get('compare-bar')) {
  customElements.define('compare-bar', CompareBar);
}
